import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { RecurrenceRule } from '../types';
import { DAY_NAMES, DAY_NAMES_FULL } from '../types';
import { buildRecurrenceLabel } from '../utils';

interface RecurrenceBuilderProps {
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
}

const FREQ_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const RRULE_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

export function RecurrenceBuilder({ value, onChange }: RecurrenceBuilderProps) {
  const handleFrequencyChange = (freq: string) => {
    if (freq === 'none') {
      onChange(null);
      return;
    }
    onChange({
      frequency: freq as RecurrenceRule['frequency'],
      interval: value?.interval || 1,
      byDay: freq === 'weekly' ? value?.byDay || [] : undefined,
      endType: value?.endType || 'never',
      count: value?.count,
      until: value?.until,
    });
  };

  const updateRule = (updates: Partial<RecurrenceRule>) => {
    if (!value) return;
    onChange({ ...value, ...updates });
  };

  return (
    <div className="space-y-3">
      {/* Frequency */}
      <div>
        <Label className="text-xs">Repeat</Label>
        <Select value={value ? value.frequency : 'none'} onValueChange={handleFrequencyChange}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQ_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value && (
        <>
          {/* Interval */}
          <div>
            <Label className="text-xs">Every</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={99}
                value={value.interval}
                onChange={(e) => updateRule({ interval: parseInt(e.target.value, 10) || 1 })}
                className="h-8 w-20 text-sm"
              />
              <span className="text-sm text-muted-foreground">
                {value.interval === 1
                  ? value.frequency.replace('ly', '')
                  : value.frequency.replace('ly', '') + 's'}
              </span>
            </div>
          </div>

          {/* Day-of-week checkboxes (weekly only) */}
          {value.frequency === 'weekly' && (
            <div>
              <Label className="text-xs">On</Label>
              <div className="flex gap-1.5 mt-1">
                {RRULE_DAYS.map((day, i) => {
                  const isChecked = value.byDay?.includes(day) ?? false;
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-label={DAY_NAMES_FULL[i]}
                      aria-pressed={isChecked}
                      className={`
                        w-8 h-8 rounded-full text-[11px] font-medium border transition-colors
                        ${isChecked ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-muted'}
                      `}
                      onClick={() => {
                        const current = value.byDay || [];
                        const next = isChecked
                          ? current.filter((d) => d !== day)
                          : [...current, day];
                        updateRule({ byDay: next });
                      }}
                    >
                      {DAY_NAMES[i][0]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* End condition */}
          <div>
            <Label className="text-xs">Ends</Label>
            <Select
              value={value.endType}
              onValueChange={(v) => updateRule({ endType: v as RecurrenceRule['endType'] })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="count">After N occurrences</SelectItem>
                <SelectItem value="until">On date</SelectItem>
              </SelectContent>
            </Select>

            {value.endType === 'count' && (
              <Input
                type="number"
                min={1}
                max={999}
                value={value.count || 10}
                onChange={(e) => updateRule({ count: parseInt(e.target.value, 10) || 10 })}
                className="h-8 w-24 text-sm mt-2"
                placeholder="Occurrences"
              />
            )}

            {value.endType === 'until' && (
              <Input
                type="date"
                value={value.until || ''}
                onChange={(e) => updateRule({ until: e.target.value })}
                className="h-8 text-sm mt-2"
              />
            )}
          </div>

          {/* Summary label */}
          <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
            {buildRecurrenceLabel(value)}
          </div>
        </>
      )}
    </div>
  );
}
